import React, {
  ReactElement,
  useRef,
  useEffect,
  useState,
  MouseEvent,
  useCallback,
} from "react";
import { Flipped } from "react-flip-toolkit";
import { ViImageData, Filter, ViImagePartDamage } from "../models";
import { makeStyles, Theme } from "@material-ui/core";
import {
  getFilterProperties,
  hasMatchingDamage,
  isMatchingDamage,
} from "../services/Utility";
import Contour from "./Contour";

const useStyle = makeStyles<Theme, { clickable: boolean; imageFaded: boolean }>(
  (theme: Theme) => ({
    wrapper: ({ clickable, imageFaded }) => ({
      position: "relative",
      display: "flex",
      height: "fit-content",
      width: "fit-content",
      placeSelf: "center",
      placeContent: "center",
      placeItems: "center",
      ".focus &": {
        opacity: 1,
      },
      ".fullView &": {
        opacity: 1,
        height: "auto",
        width: "auto",
        maxHeight: "100%",
        maxWidth: "100%",
      },
      cursor: clickable ? "pointer" : "auto",
      opacity: imageFaded ? 0.6 : 1,
      transition: "opacity 0.2s ease-out",
      "&:hover": {
        opacity: 1,
      },
    }),
    img: ({ clickable, imageFaded }) => ({
      [theme.breakpoints.down("sm")]: {
        maxHeight: "none",
        maxWidth: "100%",
      },
      ".focus &": {
        maxHeight: "40vh",
        opacity: 1,
      },
      ".fullView &": {
        height: "auto",
        width: "auto",
        maxHeight: "calc(100vh - 2 * 0.382rem)",
        maxWidth: "calc(100vw - 2 * 0.382rem)",
      },
      maxHeight: "25vh",
    }),
    contourSVG: {
      position: "absolute",
      fill: "none",
      ".fullView &": {
        height: "auto",
        width: "auto",
        maxHeight: "100%",
        maxWidth: "100%",
      },
    },
  })
);

function ImageWithContours({
  data,
  onClick,
  clickable = false,
  hoverFilter,
  isLoading,
  setHoverFilter,
}: {
  data: ViImageData;
  onClick: () => void;
  clickable?: boolean;
  hoverFilter: Filter[];
  isLoading: boolean;
  setHoverFilter?: (filters: Filter[]) => void;
}): ReactElement {
  const imgRef = useRef<HTMLImageElement>(new Image());
  const [imageFaded, setImageFaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [damages, setDamages] = useState<Array<{
    ViImagePartDamage: ViImagePartDamage;
    highlighted: boolean;
  }> | null>(null);
  const classes = useStyle({ clickable, imageFaded });

  const onContourHover = (e: MouseEvent) => {
    e.stopPropagation();
    if (setHoverFilter) {
      const contourStack = document
        .elementsFromPoint(e.clientX, e.clientY)
        .filter((node) => node.nodeName === "polygon")
        .map((node) => node.id);

      if (contourStack.length < 1) setHoverFilter([]);
      else {
        const focusDamage = data.damages
          ?.filter((damage) => contourStack?.some((id) => id === damage.id))
          .sort((a, b) => a.relativeSize - b.relativeSize)[0];
        if (focusDamage) setHoverFilter(getFilterProperties([focusDamage]));
      }
    }
  };

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  }

  const sortDamages = useCallback(
    (damages: ViImagePartDamage[]) =>
      damages
        .map((damage) => ({
          ViImagePartDamage: damage,
          highlighted: hoverFilter
            ? hoverFilter.some((filter) => isMatchingDamage(filter, damage))
            : false,
        }))
        .sort((a, b) => {
          if (a.highlighted && !b.highlighted) return 1;
          else if (!a.highlighted && b.highlighted) return -1;
          else
            return -a.ViImagePartDamage.relativeSize + b.ViImagePartDamage.relativeSize;
        }),
    [hoverFilter]
  );

  useEffect(() => {
    if (isLoading === true) return;
    setImageFaded(
      !data.damages ||
        data.damages?.length < 1 ||
        (hoverFilter.length > 0 &&
          !hoverFilter.some(
            (filter) => data.damages && hasMatchingDamage(filter, data.damages)
          ))
    );
  }, [hoverFilter, data, isLoading]);

  useEffect(() => {
    if (data.damages) setDamages(sortDamages(data.damages) || null);
  }, [hoverFilter, data, sortDamages]);

  return (
    <Flipped flipId={`imgWrapper-${data.localId}`}>
      <div className={classes.wrapper} onClick={handleClick}>
        <img
          src={data.data}
          ref={imgRef}
          className={classes.img}
          alt={data.filename}
          onLoad={() => {
            setImageLoaded(true);
            setImageDimensions({
              w: imgRef.current.naturalWidth,
              h: imgRef.current.naturalHeight,
            });
          }}
        />
        <svg
          preserveAspectRatio="xMidYMid meet"
          className={classes.contourSVG}
          {...(imageLoaded && imageDimensions
            ? {
                viewBox: `0 0 ${imageDimensions.w} ${imageDimensions.h}`,
              }
            : {})}
        >
          {imageDimensions &&
            damages &&
            damages.length > 0 &&
            damages?.map((damage) => (
              <Contour
                key={damage.ViImagePartDamage.id}
                {...{
                  dims: imageDimensions,
                  highlighted: damage.highlighted || false,
                  isFilterActive: hoverFilter.length > 0,
                  handleHover: onContourHover,
                  damage: damage.ViImagePartDamage,
                }}
              />
            ))}
        </svg>
      </div>
    </Flipped>
  );
}
export default React.memo(ImageWithContours);
